import { getServerData } from 'js/src/libs/server-data';
import { hasFeatureEnabled } from 'js/src/libs/server-utils';
import type { DAMConfig } from 'js/src/typings/global';

const Flags = {
    canUseStaffControls: (): boolean => {
        return hasFeatureEnabled('staff_controls');
    },
    canSeeDamTab: (): boolean => {
        const damConfigs = getServerData()?.USER?.dam_configs;
        if (!damConfigs) {
            return false;
        }
        return damConfigs.some((damConfig: DAMConfig) => {
            if (damConfig.require_connection && damConfig.is_connected) {
                return true;
            }
            if (!damConfig.require_connection) {
                return true;
            }
            return false;
        });
    },
    canUseToolbox: () => {
        return hasFeatureEnabled('toolbox');
    },
    canUseBrandEnvironments: () => {
        return hasFeatureEnabled('brand_environments');
    },
    canUseVideoBriefs: () => {
        return Flags.canUseToolbox() && hasFeatureEnabled('video_briefs');
    },
    chatTabDeveloperToolsEnabled: () => {
        return hasFeatureEnabled('sketch_show_dev_details');
    },
    canUseScreenRecording: () => {
        return getServerData()?.USER?.capabilities?.screen_recording ?? true;
    },
    hideAllVideoCreationOptions: () => {
        return hasFeatureEnabled('hide_all_videos_creation_options');
    },
    canUseImproveMediaAgent: () => {
        return process.env.NODE_ENV !== 'test';
    },
    canUseTransitionSFX: () => {
        return hasFeatureEnabled('sound_effects_transition');
    },
    canUseCodeo: () => {
        return hasFeatureEnabled('codeo');
    },
    canUseProductWalkthroughRecipe: () => {
        return hasFeatureEnabled('product_walkthrough_recipe');
    },
    /** The GenX animated explainer flow (style presets, ingredients, storyboard) inside the toolbox agent. */
    canUseGenxInToolbox: (): boolean => {
        return Flags.canUseToolbox() && hasFeatureEnabled('genx_toolbox');
    },
    /**
     * The pieces of codeo rolled out on their own, each as a subagent: charts, layered graphics.
     * Codeo is the overarching flag, so it brings them too.
     */
    canUseCodeoSubagents: (): boolean => {
        return Flags.canUseCodeo() || hasFeatureEnabled('codeo_subagents');
    },
    /** Team admins saving their own recipes, shown beside the built-in ones on the Create page. */
    canUseCustomRecipes: (): boolean => {
        return hasFeatureEnabled('custom_recipes');
    },
    /** The kinetic typography recipe rolled out on its own. Codeo is the overarching flag, so it brings it too. */
    canUseKineticTypography: (): boolean => {
        return Flags.canUseCodeo() || hasFeatureEnabled('kinetic_typography');
    },
    /** The Toolbox authoring graphics itself, which the kinetic typography recipe needs without the rest of codeo. */
    canAuthorMotionGraphics: (): boolean => {
        return Flags.canUseKineticTypography();
    },
    /** The SaaS product demo recipe and its product demo subagent. Codeo is the overarching flag, so it brings it too. */
    canUseSaasProductRecipe: (): boolean => {
        return Flags.canUseCodeo() || hasFeatureEnabled('saas_product_recipe');
    },
    /** Codeo authoring, the codeo subagents and the kinetic typography and SaaS product recipes all make motion graphics scenes. */
    canUseMotionGraphicsScenes: (): boolean => {
        return (
            Flags.canUseCodeoSubagents() ||
            Flags.canAuthorMotionGraphics() ||
            Flags.canUseSaasProductRecipe()
        );
    },
    canUseCodeoBrand: () => {
        return hasFeatureEnabled('codeo_brand');
    },
    canSeeAllTemplates: () => {
        return hasFeatureEnabled('view_all_templates');
    },
    /**
     * Charts are hidden for accounts that opted out of chart assets (e.g. a workspace whose chart
     * brand guidelines aren't ready yet). The flag reads as an opt-out: enrolled = charts disabled.
     */
    chartsDisabled: (): boolean => {
        return hasFeatureEnabled('disable_chartasset');
    },
    canUseDisclaimerText: () => {
        return hasFeatureEnabled('disclaimer_text');
    },
    canSeeDownloadMenuProductCallCta: () => {
        return hasFeatureEnabled('product_call_cta_download_menu');
    },
};

export default Flags;
